import React from 'react';
import { useTranslation } from 'react-i18next';
import PriceCard from '../components/PriceCard';
import TitleWithLanterns from '../components/TitleWithLanterns';
import MenuCarousel from '../components/MenuCarousel';
import { Fish, Soup, Beef, Shell, Utensils } from 'lucide-react';

const Menu: React.FC = () => {
  const { t } = useTranslation();

  const cuisineTypes = [
    { icon: <Utensils size={48} />, key: 'buffet' },
    { icon: <Beef size={48} />, key: 'grillades' },
    { icon: <Shell size={48} />, key: 'seafood' },
    { icon: <Soup size={48} />, key: 'wok' },
    { icon: <Fish size={48} />, key: 'japanese' }
  ];

  return (
    <div className="pt-16 animate-fadeIn">
      {/* Hero Section */}
      <section className="relative h-[58vh] flex items-center justify-center overflow-hidden animate-fadeInUp animation-delay-200" style={{ paddingBottom: '3rem' }}>
        {/* Background image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ 
            backgroundImage: 'url(/menu-hero-background.jpg)'
          }} 
        />
        {/* Dark overlay for better text readability */}
        <div className="absolute inset-0 bg-black/40" />
        {/* Red gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-red-900/30 via-red-800/20 to-red-900/30" />
        <div className="absolute bottom-0 left-0 right-0 h-36 bg-gradient-to-t from-white via-white/15 to-transparent" />
        
        <TitleWithLanterns 
          title={t('nav.menu')}
          subtitle={t('menu.subtitle')}
          lanternType={2}
        />
      </section>

      {/* Menu PDFs Carousel */}
      <section className="bg-white relative">
        <div className="absolute top-0 left-0 right-0 h-36 bg-gradient-to-b from-white via-white/15 to-transparent" />
        <div className="relative z-10">
          <MenuCarousel />
        </div>
      </section>

      {/* Cuisine Types */}
      <section className="py-12 md:py-20 lg:py-32 bg-koi-light relative animate-fadeInUp animation-delay-400">
        {/* Fade from white gradient at the top */}
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-white to-transparent" />
        {/* Fade to white gradient at the bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent" />
        <div className="section-padding relative z-10">
          <div className="text-center mb-8 md:mb-12 lg:mb-20">
            <p className="text-koi-red text-lg tracking-luxury uppercase font-body mb-4">{t('menu.specialties')}</p>
            <h2 className="text-display-md md:text-display-lg font-display mb-6 text-gray-800">
              {t('menu.discoverCuisines')}
            </h2>
            <div className="h-px bg-koi-red mx-auto w-20" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {cuisineTypes.map((cuisine, index) => (
              <div
                key={index}
                className="luxury-card p-6 md:p-8 lg:p-10 text-center hover-lift group"
              >
                <div className="text-koi-red mb-6 group-hover:text-gray-800 transition-colors duration-300 flex justify-center">
                  {cuisine.icon}
                </div>
                <h3 className="text-2xl font-display mb-4 text-gray-800">
                  {t(`menu.cuisine.${cuisine.key}.name`)}
                </h3>
                <p className="text-gray-700 font-body">
                  {t(`menu.cuisine.${cuisine.key}.description`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* Prices Reminder */}
      <section className="py-12 md:py-20 lg:py-32 relative animate-fadeInUp animation-delay-800">
        {/* Background image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(/prices-background.jpg)'
          }}
        />
        {/* White overlay to maintain readability */}
        <div className="absolute inset-0 bg-white/75" />
        <div className="section-padding relative z-10">
          <div className="text-center mb-8 md:mb-12 lg:mb-20">
            <p className="text-koi-red text-lg tracking-luxury uppercase font-body mb-4">{t('prices.section')}</p>
            <h2 className="text-display-md md:text-display-lg font-display mb-6 text-gray-800">
              {t('prices.title')}
            </h2>
            <div className="h-px bg-koi-red mx-auto w-20" />
          </div>

          {/* Adult Prices */}
          <div className="mb-12">
            <h3 className="text-xl font-display text-koi-red text-center mb-8">{t('prices.adultTitle')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 lg:gap-6 max-w-4xl mx-auto mb-6">
              <div>
                <PriceCard title={t('prices.lunchMonFri')} price="€17.90" />
              </div>
              <div>
                <PriceCard title={t('prices.dinnerMonFri')} price="€24.90" />
              </div>
              <div>
                <PriceCard title={t('prices.weekendHoliday')} price="€25.90" />
              </div>
            </div>
          </div>

          {/* Children Prices */}
          <div className="mb-12">
            <h3 className="text-xl font-display text-koi-red text-center mb-8">{t('prices.childTitle')}</h3>
            
            {/* 10-12 Years */}
            <div className="mb-8">
              <h4 className="text-lg font-display text-gray-800 text-center mb-4">{t('prices.children1012')}</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 lg:gap-6 max-w-4xl mx-auto">
                <div>
                  <PriceCard title={t('prices.lunchMonFri')} price="€13.90" />
                </div>
                <div>
                  <PriceCard title={t('prices.dinnerMonFri')} price="€19.90" />
                </div>
                <div>
                  <PriceCard title={t('prices.weekendHoliday')} price="€20.90" />
                </div>
              </div>
            </div>
            
            {/* 3-10 Years */}
            <div className="mb-8">
              <h4 className="text-lg font-display text-gray-800 text-center mb-4">{t('prices.children310')}</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 lg:gap-6 max-w-4xl mx-auto">
                <div>
                  <PriceCard title={t('prices.lunchMonFri')} price="€8.90" />
                </div>
                <div>
                  <PriceCard title={t('prices.dinnerMonFri')} price="€10.00" />
                </div>
                <div>
                  <PriceCard title={t('prices.weekendHoliday')} price="€11.90" />
                </div>
              </div>
            </div>
            
            {/* Under 3 */}
            <div className="text-center">
              <h4 className="text-lg font-display text-gray-800 mb-4">{t('prices.childrenUnder3')}</h4>
              <p className="text-2xl font-bold text-koi-red">{t('prices.free')}</p>
            </div>
          </div>

          <p className="text-center text-gray-800/70 mt-8 text-sm font-body italic">
            {t('prices.disclaimer')}
          </p>

        </div>
      </section>
    </div>
  );
};

export default Menu;